import React, { useState } from 'react';
import { Translate } from '../../shared/LanguageService';
import { Table, Button, Box, Chip, Typography } from '@mui/material';
import IPhotographer from './IPhotographer';
import PhotographersListRow from './PhotographersListRow';
import SendEmailToPhotographersModal from './SendEmailToPhotographersModal';

interface IPhotographersListProps {
    photographers: IPhotographer[];
    edit: (id: number) => void;
    logs: (id: number) => void;
    showDiscontinued: boolean;
}

const PhotographersList = (props: IPhotographersListProps) => {
    const [showSendEmailToPhotographersModal, setShowSendEmailToPhotographersModal] = useState(false);
    const [photographersInRegion, setPhotographersInRegion] = useState<IPhotographer[]>([]);

    function getRegions(photographers: IPhotographer[]) {
        return photographers
            .map((p) => p.region)
            .filter((value, index, self) => self.indexOf(value) === index);
    }

    function filterPhotographer(photographer: IPhotographer) {
        if (props.showDiscontinued) {
            return true;
        }
        return !photographer.isDiscontinued;
    }

    function sortPhotographer(a: IPhotographer, b: IPhotographer) {
        return a.name.localeCompare(b.name);
    }

    function handleOpenSendEmailToPhotographersInRegionDialog(region: string) {
        const photographersInRegion = props.photographers.filter((p) => p.region === region && p.isDiscontinued === false);
        setPhotographersInRegion(photographersInRegion);
        setShowSendEmailToPhotographersModal(true);
    }

    const visiblePhotographers = props.photographers.filter(filterPhotographer);

    return (
        <div>
            {getRegions(props.photographers)
                .sort((a, b) => a.localeCompare(b))
                .map((region) => {
                    const photographersInRegion = visiblePhotographers.filter(
                        (photographer) => photographer.region === region
                    );
                    return (
                        <div key={`region-${region.toLowerCase()}`} style={{ marginBottom: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h2>{region}</h2>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => handleOpenSendEmailToPhotographersInRegionDialog(region)}
                                >
                                    {Translate('photographer/send-email-by-region/button-send-email')}
                                </Button>
                            </div>
    
                            <Table>
                                <thead>
                                    <tr>
                                        <th>{Translate('photographer/list/header/name')}</th>
                                        <th>{Translate('photographer/list/header/email-address')}</th>
                                        <th>{Translate('photographer/list/header/phone-number')}</th>
                                        <th>{Translate('photographer/list/header/cubicasa')}</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {photographersInRegion
                                        .sort(sortPhotographer)
                                        .map((u: IPhotographer) => {
                                            return (
                                                <PhotographersListRow
                                                    photographer={u}
                                                    key={u.id}
                                                    edit={props.edit}
                                                    logs={props.logs}
                                                />
                                            );
                                        })}
                                </tbody>
                            </Table>
                            <Box
                                data-testid={`region-photographers-count-${region.toLowerCase()}`}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    alignItems: 'center',
                                    gap: 1,
                                    mt: 1
                                }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    {Translate('photographer/list/summary/photographers-in-region')}
                                </Typography>
                                <Chip
                                    label={photographersInRegion.length}
                                    size="small"
                                    color="primary"
                                />
                            </Box>
                        </div>
                    );
                })}
    
            <Box
                data-testid="total-photographers-count"
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderTop: 1,
                    borderColor: 'divider',
                    pt: 2,
                    mt: 3
                }}
            >
                <Typography variant="h6" component="span">
                    {Translate('photographer/list/summary/photographers-total')}
                </Typography>
                <Typography
                    variant="h4"
                    component="span"
                    color="primary"
                    sx={{ fontWeight: 'bold' }}
                >
                    {visiblePhotographers.length}
                </Typography>
            </Box>

            <SendEmailToPhotographersModal
                open={showSendEmailToPhotographersModal}
                photographers={photographersInRegion}
                onClose={() => setShowSendEmailToPhotographersModal(false)}
            />
        </div>
    );
};

export default PhotographersList;